import React from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import Step from '@material-ui/core/Step';
import StepButton from '@material-ui/core/StepButton';
import StepLabel from '@material-ui/core/StepLabel';
import Stepper from '@material-ui/core/Stepper';
import { withStyles, createStyles, WithStyles } from '@material-ui/core/styles';

import { addJob } from '../../action/job/add-job';
import { loadJobDetails } from '../../action/job/load-job-details';
import { updateJob } from '../../action/job/update-job';
import { loadJob } from '../../job/actions/load-job';
import { jobSelector, jobDetailsSelector } from '../../reducers/job-selectors';
import { orgSelector } from '../../reducers/org-selectors';

import { UiButton } from '../../ui/ui-button';

import { JobFlowApplicantFilters } from './job-flow-applicant-filters';
import { JobFlowCompensation } from './job-flow-compensation';
import { JobFlowConfirmation } from './job-flow-confirmation';
import { JobFlowDescription } from './job-flow-description';
import { JobFlowDetails } from './job-flow-details';
import { JobFlowExperience } from './job-flow-experience';
import { JobFlowImage } from './job-flow-image';
import { JobFlowLicenses } from './job-flow-licenses';
import { JobFlowNotifications } from './job-flow-notifications';
import { JobFlowPreHireSteps } from './job-flow-pre-hire-steps';
import { JobFlowShortDescription } from './job-flow-short-description';

import { ICON_NAMES, MaterialDesignIcon } from '../../util/material-design-icons';
import { NOTIFICATION_TYPES } from '../../util/notification-types';
import Message from '../../widgets/legacy-message';
import { LoadingMessage } from '../../widgets/loading-message';

const styles = () =>
  createStyles({
    dialogPaper: {
      height: 'calc(100% - 64px)',
      maxWidth: '800px',
    },
  });

const mapStateToProps = (state: any, { jobId, orgId }: InputProps) => ({
  org: orgSelector(state, orgId),
  job: jobId ? jobSelector(state, jobId) : null,
  jobDetails: jobId ? jobDetailsSelector(state, jobId) : null,
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { jobId, orgId }: InputProps,
) => ({
  actions: {
    addJob: (job: Record<string, any>) => dispatch(addJob(orgId, job, null)),
    updateJob: (job: Record<string, any>, isEditing: boolean) =>
      jobId && dispatch(updateJob(jobId, job, isEditing)),
    loadJobDetails: () => jobId && dispatch(loadJobDetails(jobId)),
    loadJob: () => jobId && dispatch(loadJob(jobId)),
  },
});

interface InputProps {
  updatingSourcingInfo?: boolean;
  orgId: number;
  editing: boolean;
  onClose: Function;
  onFinished: () => void;

  /**
   * The step that the job flow will open to. Use a constant exported by this
   * component.
   * @see JOB_FLOW_DESCRIPTION_STEP
   */
  initialStep?: number;

  jobId?: number | null;
  onCreate?: (jobId: number | null) => void;
}

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & StateProps & DispatchProps & WithStyles<typeof styles>;

type State = {
  step: number;
  saving: boolean;
  dirty: boolean;
  onContinueWithoutSaving: Function | null;
  confirmationDialogVisible: boolean;
};
export const JOB_FLOW_DESCRIPTION_STEP = 2;
export const JOB_FLOW_SUMMARY_AND_TAGLINE_STEP = 3;
export const JOB_FLOW_PRE_HIRE_STEP = 8;

class AddJobFlowComponent extends React.PureComponent<Props, State> {
  static defaultProps = {
    updatingSourcingInfo: false,
    initialStep: 0,
  };

  state: State = {
    step: this.props.updatingSourcingInfo
      ? JOB_FLOW_SUMMARY_AND_TAGLINE_STEP
      : this.props.initialStep ?? 0,
    saving: false,
    dirty: false,
    onContinueWithoutSaving: null,
    confirmationDialogVisible: false,
  };

  componentDidMount() {
    this.props.actions.loadJobDetails();
    this.props.actions.loadJob();
  }

  componentDidUpdate() {
    this.props.actions.loadJobDetails();
    this.props.actions.loadJob();
  }

  nextStep = async (job: Partial<Job>) => {
    const { actions, onCreate, editing: isEditing } = this.props;
    this.setState({ saving: true });

    if (this.props.job) {
      await actions.updateJob(job, isEditing);
    } else {
      const response = await actions.addJob(job);
      if (onCreate) {
        onCreate(response.job.job_id);
      }
    }

    this.setState((prevState) => ({
      saving: false,
      dirty: false,
      step: isEditing ? prevState.step : prevState.step + 1,
    }));
  };

  skipStep = () => this.setState((prevState) => ({ step: prevState.step + 1 }));

  prevStep = () => this.setState((prevState) => ({ step: prevState.step - 1 }));

  handleStepClick = (step: number) => () => {
    if (this.state.dirty) {
      this.setState({
        confirmationDialogVisible: true,
        onContinueWithoutSaving: () =>
          this.setState({
            step,
            dirty: false,
          }),
      });
      return;
    }

    this.setState({
      step,
      dirty: false,
    });
  };

  jobHasTextNotifications = () => {
    const { job, jobDetails } = this.props;
    if (!job || !jobDetails) {
      return false;
    }
    const { forwardingPhoneNumber } = job;
    const { notificationSettings } = jobDetails;
    return (
      forwardingPhoneNumber ||
      (notificationSettings &&
        notificationSettings.find(
          (setting) => setting.notificationType === NOTIFICATION_TYPES.INCOMING_TEXT_NOTIFICATION,
        ))
    );
  };

  orgHasTextNotificationDefaults = () => {
    const { org } = this.props;
    return (
      org &&
      (org.forwardingPhoneNumber ||
        org.NotificationSettings.find(
          (s) => s.notificationType === NOTIFICATION_TYPES.INCOMING_TEXT_NOTIFICATION,
        ))
    );
  };

  handleContinueWithoutSaving = (continueWithoutSaving: boolean) => () => {
    const { onContinueWithoutSaving } = this.state;
    if (continueWithoutSaving && onContinueWithoutSaving) {
      onContinueWithoutSaving();
    }
    this.setState({
      confirmationDialogVisible: false,
      onContinueWithoutSaving: null,
    });
  };

  handleDirtyJob = (dirty: boolean) => {
    this.setState({ dirty });
  };

  handleClose = () => {
    if (this.state.dirty) {
      this.setState({
        confirmationDialogVisible: true,
        onContinueWithoutSaving: this.props.onClose,
      });
      return;
    }

    this.props.onClose();
  };

  createAnotherJob = () => {
    if (this.props.onCreate) {
      this.props.onCreate(null);
    }
    this.setState({
      step: 0,
    });
  };

  render() {
    const { updatingSourcingInfo, orgId, onFinished, classes, jobDetails, jobId, editing, job } =
      this.props;
    const { step, saving, confirmationDialogVisible } = this.state;
    const { prevStep, nextStep, skipStep } = this;

    const editingJob: Partial<Job> = { ...this.props.job };
    const stepProps = {
      onNext: nextStep,
      onPrev: prevStep,
      onSkip: skipStep,
      onDirty: this.handleDirtyJob,
      editing,
      saving,
    };

    /**
     * NOTE: The order of these steps matter and are exported at the top of this component
     * @see JOB_FLOW_SUMMARY_AND_TAGLINE_STEP
     */
    const steps = [
      {
        label: 'Details',
        component: <JobFlowDetails job={editingJob || {}} orgId={orgId} {...stepProps} />,
        completed: Boolean(jobId),
      },
      {
        label: 'Compensation',
        component: <JobFlowCompensation job={editingJob} {...stepProps} />,
        completed: Boolean(editingJob.compensationLow || editingJob.compensationHigh),
      },
      {
        label: 'Description',
        component: <JobFlowDescription job={editingJob} {...stepProps} />,
        completed: Boolean(editingJob.description),
      },
      {
        label: 'Summary & Tagline',
        component: <JobFlowShortDescription job={editingJob} orgId={orgId} {...stepProps} />,
        completed: Boolean(editingJob.shortDescription && editingJob.tagline),
      },
      {
        label: 'Image',
        component: <JobFlowImage orgId={orgId} job={editingJob} {...stepProps} />,
        completed: Boolean(editingJob.imagePath && editingJob.fullResImagePath),
      },
      {
        label: 'Experience',
        component: (
          <JobFlowExperience
            jobId={editingJob.job_id}
            jobRoleId={editingJob.jobRoleId}
            {...stepProps}
          />
        ),
        completed: Boolean(jobDetails && jobDetails.relevantExperience.length),
      },
      {
        label: 'Licenses',
        component: (
          <JobFlowLicenses
            jobId={editingJob.job_id}
            jobRoleId={editingJob.jobRoleId}
            {...stepProps}
          />
        ),
        completed: Boolean(jobDetails && jobDetails.relevantLicenses.length),
      },
      {
        label: 'Applicant Filters',
        component: <JobFlowApplicantFilters jobId={editingJob.job_id} {...stepProps} />,
        completed: Boolean(
          job?.HqcSettings?.filter((hqc) => !hqc.AutomatedLabel?.isAutoManaged)?.length,
        ),
      },
      {
        label: 'Pre-hire Steps',
        component: <JobFlowPreHireSteps jobId={editingJob?.job_id} {...stepProps} />,
        completed: Boolean(
          jobDetails && jobDetails.JobFinalizingHireSteps.filter((s) => s.isActive).length,
        ),
      },
    ];

    // IF (11/14/19): Only want this step if there aren't already org level defaults setup
    if (!editing && !this.orgHasTextNotificationDefaults()) {
      steps.splice(1, 0, {
        label: 'Notifications',
        component: <JobFlowNotifications job={editingJob} {...stepProps} />,
        completed: Boolean(jobDetails && this.jobHasTextNotifications()),
      });
    }

    // IF (02/18/20): Only want this step if the user is adding a new job and going through the
    // steps sequentially
    if (!editing) {
      steps.push({
        label: 'Complete',
        component: (
          <JobFlowConfirmation
            {...stepProps}
            onFinished={onFinished}
            onCreateAnother={this.createAnotherJob}
          />
        ),
        completed: false,
      });
    }

    let updateSourceInfoComponent = null;
    if (updatingSourcingInfo) {
      const components = [];
      let combinedComponent = null;

      const summaryStepIndex = steps.findIndex((s) => s.label === 'Summary & Tagline');
      const imageStepIndex = steps.findIndex((s) => s.label === 'Image');

      if (!steps[summaryStepIndex].completed) {
        components.push(
          <UiButton variant="tertiary" size="xxs" onClick={this.handleStepClick(summaryStepIndex)}>
            Summary & Tagline
          </UiButton>,
        );
      }

      if (!steps[imageStepIndex].completed) {
        components.push(
          <UiButton variant="tertiary" size="xxs" onClick={this.handleStepClick(imageStepIndex)}>
            Image
          </UiButton>,
        );
      }

      switch (components.length) {
        case 1:
          combinedComponent = <>{components[0]} step</>;
          break;
        case 2:
          combinedComponent = (
            <>
              {components[0]} and {components[1]} step
            </>
          );
          break;
        default:
          combinedComponent = null;
      }

      if (combinedComponent) {
        updateSourceInfoComponent = (
          <Message canBeDismissed={false} variant="warning">
            To start advertising, make sure to complete the {combinedComponent}
          </Message>
        );
      }
    }

    return (
      <>
        <Dialog
          onClose={this.handleClose}
          open
          fullWidth
          classes={{ paper: classes.dialogPaper }}
          disableEnforceFocus
        >
          <DialogTitle
            disableTypography
            className="u-background-primary u-display-flex u-justifyContent-spaceBetween u-alignItems-center u-no-padding"
          >
            <div />
            <div className="typo-h6 u-color-white">{editing ? 'Edit' : 'Add'} Job</div>
            <IconButton onClick={this.handleClose}>
              <MaterialDesignIcon iconName={ICON_NAMES.CLOSE} color="white" />
            </IconButton>
          </DialogTitle>
          {editing && !job ? (
            <LoadingMessage />
          ) : (
            <>
              {updateSourceInfoComponent}
              <div className="u-display-flex u-alignItems-flexStart u-background-offWhite u-height-100">
                <Stepper
                  orientation="vertical"
                  nonLinear
                  activeStep={step}
                  classes={{ root: 'u-background-offWhite' }}
                >
                  {steps.map((s, index) => (
                    <Step
                      key={s.label}
                      active={index === step}
                      disabled={!jobId}
                      completed={s.completed}
                    >
                      <StepButton
                        icon={editing ? '--' : index + 1}
                        className={`u-textAlign-left ${
                          step === index ? 'u-background-lightGray' : ''
                        }`}
                        onClick={this.handleStepClick(index)}
                      >
                        <StepLabel>{s.label}</StepLabel>
                      </StepButton>
                    </Step>
                  ))}
                </Stepper>
                <div className="u-background-offWhite u-padding-bottom-2x u-padding-right-2x">
                  {steps[step].component}
                </div>
              </div>
            </>
          )}
        </Dialog>
        {confirmationDialogVisible && (
          <Dialog open fullWidth maxWidth="xs" disableEnforceFocus>
            <DialogTitle>Continue without saving?</DialogTitle>
            <DialogContent>All your changes will be lost.</DialogContent>
            <DialogActions>
              <UiButton variant="dismiss" onClick={this.handleContinueWithoutSaving(true)}>
                Discard Changes
              </UiButton>
              <UiButton onClick={this.handleContinueWithoutSaving(false)}>Go Back</UiButton>
            </DialogActions>
          </Dialog>
        )}
      </>
    );
  }
}

export const AddJobFlow = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(styles)(AddJobFlowComponent));
